import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaCode, FaUsers, FaRocket, FaAward, FaLightbulb, FaHeart } from 'react-icons/fa';
import BookAppointment from '../components/BookAppointment';
import logo from '../assets/logo.png';
import aboutImage from '../assets/about.jpg';

const AboutSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled(motion.div)`
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1.5rem;
    color: #ffffff;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #cccccc;
    margin-bottom: 1.5rem;
  }
`;

const AboutImage = styled(motion.div)`
  position: relative;
  
  .image-placeholder {
    width: 100%;
    height: 400px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, rgba(70, 130, 180, 0.05), transparent);
      animation: shimmer 3s infinite;
    }
    
    @keyframes shimmer {
      0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
      100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
    }
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: #1a1a1a;
  border-radius: 15px;
  border: 1px solid rgba(70, 130, 180, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #4682B4;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #cccccc;
`;

const ValuesSection = styled.div`
  margin-top: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 3rem;
  color: #ffffff;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const ValueCard = styled(motion.div)`
  background: #1a1a1a;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: #4682B4;
  margin-bottom: 1rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const ValueDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
`;

const ExpertiseSection = styled.div`
  margin-top: 4rem;
  background: #1a1a1a;
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid rgba(70, 130, 180, 0.1);
`;

const ExpertiseList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const ExpertiseItem = styled(motion.li)`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #2a2a2a;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(70, 130, 180, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(70, 130, 180, 0.3);
    transform: translateY(-2px);
  }
  
  .icon {
    color: #4682B4;
    margin-right: 1rem;
    font-size: 1.2rem;
  }
  
  .text {
    color: #ffffff;
    font-weight: 500;
  }
`;

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const values = [
    {
      icon: <FaCode />,
      title: "Clean Code",
      description: "Writing maintainable, scalable, and well-documented code that follows industry best practices."
    },
    {
      icon: <FaUsers style={{ color: '#8B4789' }} />,
      title: "Client Focus",
      description: "Understanding client needs and delivering solutions that exceed expectations."
    },
    {
      icon: <FaRocket style={{ color: '#DC143C' }} />,
      title: "Innovation",
      description: "Staying updated with latest technologies and implementing creative solutions."
    },
    {
      icon: <FaAward style={{ color: '#25D366' }} />,
      title: "Quality",
      description: "Ensuring high-quality deliverables through rigorous testing and attention to detail."
    },
    {
      icon: <FaLightbulb style={{ color: '#ffd700' }} />,
      title: "Problem Solving",
      description: "Tackling complex challenges with analytical thinking and creative approaches."
    },
    {
      icon: <FaHeart style={{ color: '#FF8C00' }} />,
      title: "Passion",
      description: "Bringing enthusiasm and dedication to every project we undertake."
    }
  ];

  const expertise = [
    "E-commerce Development",
    "Business Websites",
    "Custom Web Applications",
    "Shopify Development",
    "WordPress Development",
    "React.js Applications",
    "Responsive Design",
    "Performance Optimization",
    "SEO Implementation",
    "API Integration",
    "Database Design",
    "UI/UX Development"
  ];

  return (
    <AboutSection>
      <AboutContainer>
        <AboutGrid>
          <AboutContent
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants}>
              About Softvance Solution
            </motion.h2>
            <motion.p variants={itemVariants}>
              Softvance Solution is a premier web development company with over 5 years of experience in creating 
              modern, responsive, and high-performance websites. Our journey in web development 
              started with a curiosity about how things work on the internet, and it has evolved 
              into a dedicated company focused on helping businesses establish their digital presence.
            </motion.p>
            <motion.p variants={itemVariants}>
              We specialize in building e-commerce stores, business websites, and custom web 
              solutions using technologies like React, Shopify, WordPress, HTML, CSS, and JavaScript. 
              Our approach combines technical expertise with creative problem-solving to deliver 
              exceptional digital experiences for our clients.
            </motion.p>
            <motion.p variants={itemVariants}>
              When we're not coding, you'll find our team exploring new technologies, contributing to 
              open-source projects, or sharing knowledge with the developer community. We believe 
              in continuous learning and staying updated with the latest industry trends to provide 
              the best solutions for our clients.
            </motion.p>
          </AboutContent>

          <AboutImage
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src={aboutImage} 
              alt="Softvance Solution Team" 
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }} 
            />
          </AboutImage>
        </AboutGrid>

        <StatsSection>
          <StatCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <StatNumber>50+</StatNumber>
            <StatLabel>Projects Completed</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <StatNumber>30+</StatNumber>
            <StatLabel>Happy Clients</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <StatNumber>5+</StatNumber>
            <StatLabel>Years Experience</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <StatNumber>15+</StatNumber>
            <StatLabel>Technologies</StatLabel>
          </StatCard>
        </StatsSection>

        <ValuesSection>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Core Values
          </SectionTitle>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesSection>

        <ExpertiseSection>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Areas of Expertise
          </SectionTitle>
          <ExpertiseList>
            {expertise.map((item, index) => (
              <ExpertiseItem
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <span className="icon">✓</span>
                <span className="text">{item}</span>
              </ExpertiseItem>
            ))}
          </ExpertiseList>
        </ExpertiseSection>
      </AboutContainer>
      
      <div style={{ marginTop: '4rem' }}>
        <BookAppointment />
      </div>
    </AboutSection>
  );
};

export default About;
